import type {
  Asset,
  AssetTypes,
  EditAssetWorkflowOptions,
  AssetWorkflowStepOptions,
  AssetToWorkflowStateMapping,
  WorkflowContext,
  AssetState,
} from './types';
import { AssetWorkflow } from './AssetWorkflow';
import type { JSONSchema7Definition } from 'json-schema';
import Ajv from 'ajv';
import { workflowValidationSchema } from './workflowValidationSchema';
import { EditAssetWorkflowStep } from './EditAssetWorkflowStep';
import type { AssetWorkflowStep } from './AssetWorkflowStep';

const ajv = new Ajv();

export class EditAssetWorkflow extends AssetWorkflow {
  private readonly _assetWorkflowKey: string;
  public readonly assetId: string;
  public readonly assetIdKey: string;
  public readonly archived: boolean;
  public readonly assetType: AssetTypes;
  public readonly active: boolean;
  public readonly asset: Asset;
  public readonly enabled: boolean;
  public readonly assetSchemaMatcher?: JSONSchema7Definition;
  public readonly assetValidator: Ajv.ValidateFunction;

  constructor(
    workflowGroupKey: string,
    options: EditAssetWorkflowOptions,
    asset: Asset,
    assetState: AssetState,
    isShipiumAdmin?: boolean,
  ) {
    super(workflowGroupKey, options, assetState, isShipiumAdmin);
    this._assetWorkflowKey = options.workflowKey;
    this.assetIdKey = AssetWorkflow.getAssetIdKeyForAssetType(options.assetType);
    this.assetId = (asset as any)[this.assetIdKey];
    this.archived = asset.archived ?? false;
    this.active = asset.active ?? true;
    this.asset = asset;
    this.assetType = options.assetType;
    this.assetSchemaMatcher = options.assetSchemaMatcher;
    this.workflowSteps = EditAssetWorkflow.constructSteps(
      this.workflowKey,
      options.workflowStepOptions,
      assetState,
      isShipiumAdmin,
      asset,
    );
    this.workflowStepMap = AssetWorkflow.constructStepMap(this.workflowSteps);
    this.workflowConditionalValidator = workflowValidationSchema.compile(
      this.workflowConditionalMatcher,
      this.workflowKey,
      assetState,
    );
    if (options.assetSchemaMatcher) {
      this.assetValidator = ajv.compile(options.assetSchemaMatcher);
      this.enabled = !!this.assetValidator(asset);
    } else {
      this.assetValidator = ajv.compile({ type: 'object', additionalProperties: true });
      this.enabled = true;
    }
  }

  public static constructSteps(
    workflowKey: string,
    workflowStepOptions: AssetWorkflowStepOptions[],
    assetState: AssetState,
    isShipiumAdmin?: boolean,
    asset?: Asset,
  ): AssetWorkflowStep[] {
    if (asset) {
      return workflowStepOptions
        .filter((option) => isShipiumAdmin || !option.shipiumOnly)
        .map((option, index) => {
          option.workflowStepIndex = option.workflowStepIndex || index + 1;
          return option;
        })
        .sort((a, b) =>
          (a.workflowStepIndex || 0) < (b.workflowStepIndex || 0)
            ? -1
            : (a.workflowStepIndex || 0) > (b.workflowStepIndex || 0)
              ? 1
              : 0,
        )
        .map((options) => new EditAssetWorkflowStep(workflowKey, options, asset, assetState));
    }
    return [];
  }
  private calculateWorkflowStateFromMapping(asset: any, assetToWorkflowMapping: AssetToWorkflowStateMapping) {
    if (assetToWorkflowMapping.assetProperty) {
      const property = asset[assetToWorkflowMapping.assetProperty];
      if (assetToWorkflowMapping.computeFn) {
        return assetToWorkflowMapping.computeFn(property);
      }
      return property;
    }
    if (assetToWorkflowMapping.assetProperties) {
      const properties: Record<string, unknown> = {};
      for (const prop of assetToWorkflowMapping.assetProperties) {
        properties[prop] = asset[prop];
      }
      if (assetToWorkflowMapping.computeFn) {
        return assetToWorkflowMapping.computeFn(properties);
      }
      return properties;
    }
  }

  public getExistingLinkValuesForLinkedAsset(linkAssetType: AssetTypes) {
    const linkProperties = AssetWorkflow.getLinkPropertiesForAssetType(this.assetType);
    if (linkProperties) {
      for (const key of Object.keys(linkProperties)) {
        if (linkProperties[key] === linkAssetType) {
          return (this.asset as any)[key] || [];
        }
      }
    }
    return [];
  }

  public mapAssetInitialWorkflowState(): Record<string, any> {
    return this.workflowSteps.reduce<Record<string, any>>((prev, curr) => {
      try {
        const { assetToWorkflowMapping, workflowStateToAssetMapping, workflowStepKey } = curr;
        const asset = this.asset as any;
        if (assetToWorkflowMapping) {
          prev[workflowStepKey] = this.calculateWorkflowStateFromMapping(asset, assetToWorkflowMapping);
        }
        if (
          !assetToWorkflowMapping &&
          workflowStateToAssetMapping &&
          workflowStateToAssetMapping.mappingType === 'property' &&
          workflowStateToAssetMapping.excludeFromInitalState !== true
        ) {
          const value =
            asset[workflowStateToAssetMapping.property] === null
              ? undefined
              : asset[workflowStateToAssetMapping.property];
          prev[workflowStepKey] = value;
        }
        if (
          !assetToWorkflowMapping &&
          workflowStateToAssetMapping &&
          workflowStateToAssetMapping.mappingType === 'properties' &&
          workflowStateToAssetMapping.excludeFromInitalState !== true
        ) {
          workflowStateToAssetMapping.properties.forEach((p) => {
            const value = asset[p] === null ? undefined : asset[p];
            prev[workflowStepKey] = value;
          });
        }
        if (
          !assetToWorkflowMapping &&
          workflowStateToAssetMapping &&
          workflowStateToAssetMapping.mappingType === 'nestedProperty' &&
          workflowStateToAssetMapping.excludeFromInitalState !== true
        ) {
          const parent = asset[workflowStateToAssetMapping.parent] || {};
          const value =
            parent[workflowStateToAssetMapping.property] === null
              ? undefined
              : parent[workflowStateToAssetMapping.property];
          // prev[workflowStepKey].parent = prev[workflowStepKey].parent || {};
          // ^ we had this in before
          // I am not sure what it was supposed to do but as far as
          // I can tell it would just get overwritten
          prev[workflowStepKey] = value;
        }
        if (
          !assetToWorkflowMapping &&
          workflowStateToAssetMapping &&
          workflowStateToAssetMapping.mappingType === 'link' &&
          workflowStateToAssetMapping.excludeFromInitalState !== true
        ) {
          let idField;
          if (workflowStateToAssetMapping.linkAssetTypeOverride) {
            idField = AssetWorkflow.getAssetIdKeyForOverride(workflowStateToAssetMapping.linkAssetTypeOverride);
          } else {
            idField = AssetWorkflow.getAssetIdKeyForAssetType(workflowStateToAssetMapping.linkAssetType);
          }
          // a little hack that I added to allow auto population of the multiselect contracts to work
          // the fulfillment context's property is contractIds on fulfillment context
          idField = workflowStateToAssetMapping?.list ? `${idField}s` : idField;
          const value = asset[idField] === null ? undefined : asset[idField];
          prev[workflowStepKey] = value;
        }
      } catch (e) {
        console.error(e);
      }
      return prev;
    }, {});
  }

  public get workflowKey(): string {
    return `${this._assetWorkflowKey}-${this.assetId}`;
  }

  public buildAssetActionPayload(workflowContext: WorkflowContext) {
    const assetActionPayload = super.buildAssetActionPayload(workflowContext);
    for (const step of this.workflowSteps) {
      const stepValue = workflowContext.workflowState[this.workflowKey][step.workflowStepKey];

      if (step.workflowStateToAssetMapping?.mappingType === 'link') {
        if (Array.isArray(stepValue)) {
          const existingLinks = this.getExistingLinkValuesForLinkedAsset(
            step.workflowStateToAssetMapping.linkAssetType,
          );
          // for all existing links, check to see if the stepValue contains the link if not, unlink
          // in the special origin set case we will have an exisisting link of type
          // string but a step value of string[] so we need to check both are arrays
          if (Array.isArray(existingLinks)) {
            for (const link of existingLinks) {
              if (!stepValue.includes(link)) {
                assetActionPayload.assetLinks.push({
                  assetType: step.workflowStateToAssetMapping.linkAssetType,
                  assetId: link,
                  unlink: true,
                  linkType: step.workflowStateToAssetMapping.linkType,
                  linkAssetTypeOverride: step.workflowStateToAssetMapping.linkAssetTypeOverride,
                });
              }
            }
          }
          // for all the updated links, check to see if there is already an existing link, if there is a new link add it
          for (const link of stepValue) {
            if (!existingLinks.includes(link)) {
              assetActionPayload.assetLinks.push({
                assetType: step.workflowStateToAssetMapping.linkAssetType,
                assetId: link,
                linkType: step.workflowStateToAssetMapping.linkType,
                linkAssetTypeOverride: step.workflowStateToAssetMapping.linkAssetTypeOverride,
              });
            }
          }
        }
      }
    }
    return assetActionPayload;
  }
}
